import React from "react";

import {
  formatMoney,
  formatPercent,
} from "../../../utils/formatters";

export default function CoverageExecutiveInsight({
  summary = {},
  settings,
}) {
  const currency =
    settings?.currency || "USD";

  const scenariosCount = Number(
    summary?.scenarios_count ?? 0
  );

  const uncoveredCount = Number(
    summary?.uncovered_count ?? 0
  );

  const underinsuredCount = Number(
    summary?.underinsured_count ?? 0
  );

  const marginalCount = Number(
    summary?.marginal_count ?? 0
  );

  const adequateCount = Number(
    summary?.adequate_count ?? 0
  );

  const capitalAtRisk = Number(
    summary?.total_capital_at_risk ??
      summary?.portfolio_gap ??
      0
  );

  const protectionRatio = normalizeRatio(
    summary?.portfolio_protection_ratio
  );

  const attentionCount =
    uncoveredCount +
    underinsuredCount +
    marginalCount;

  const tone = getInsightTone({
    scenariosCount,
    uncoveredCount,
    underinsuredCount,
    marginalCount,
    protectionRatio,
  });

  const insight = buildInsight({
    scenariosCount,
    uncoveredCount,
    underinsuredCount,
    marginalCount,
    adequateCount,
    protectionRatio,
    capitalAtRisk,
    currency,
  });

  const styles = {
    critical:
      "border-red-200 bg-red-50",
    warning:
      "border-amber-200 bg-amber-50",
    positive:
      "border-emerald-200 bg-emerald-50",
    neutral:
      "border-slate-200 bg-slate-50",
  };

  const accentStyles = {
    critical: "bg-red-500",
    warning: "bg-amber-500",
    positive: "bg-emerald-500",
    neutral: "bg-slate-400",
  };

  return (
    <section
      className={`relative overflow-hidden rounded-xl border p-5 ${
        styles[tone]
      }`}
    >
      <div
        className={`absolute inset-y-0 left-0 w-1.5 ${
          accentStyles[tone]
        }`}
      />

      <div className="pl-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Executive Insight
        </p>

        <h3 className="mt-2 text-base font-semibold text-slate-900">
          {insight.title}
        </h3>

        <p className="mt-2 max-w-5xl text-sm leading-6 text-slate-700">
          {insight.description}
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <InsightMetric
            label="Financial Protection"
            value={formatPercent(
              protectionRatio,
              1
            )}
          />

          <InsightMetric
            label="Capital at Risk"
            value={formatMoney(
              capitalAtRisk,
              currency
            )}
          />

          <InsightMetric
            label="Scenarios Requiring Attention"
            value={`${attentionCount} of ${scenariosCount}`}
          />
        </div>

        <div className="mt-4 rounded-lg border border-white/70 bg-white/60 px-4 py-3">
          <p className="text-xs font-semibold text-slate-500">
            Management Priority
          </p>

          <p className="mt-1 text-sm font-medium text-slate-900">
            {insight.recommendation}
          </p>
        </div>
      </div>
    </section>
  );
}

function InsightMetric({
  label,
  value,
}) {
  return (
    <div className="rounded-lg border border-white/80 bg-white/70 px-3 py-2">
      <p className="text-xs text-slate-500">
        {label}
      </p>

      <p className="mt-1 text-sm font-semibold text-slate-900">
        {value}
      </p>
    </div>
  );
}

function getInsightTone({
  scenariosCount,
  uncoveredCount,
  underinsuredCount,
  marginalCount,
  protectionRatio,
}) {
  if (scenariosCount === 0) {
    return "neutral";
  }

  if (
    uncoveredCount > 0 ||
    protectionRatio < 0.5
  ) {
    return "critical";
  }

  if (
    underinsuredCount > 0 ||
    marginalCount > 0 ||
    protectionRatio < 0.8
  ) {
    return "warning";
  }

  return "positive";
}

function buildInsight({
  scenariosCount,
  uncoveredCount,
  underinsuredCount,
  marginalCount,
  adequateCount,
  protectionRatio,
  capitalAtRisk,
  currency,
}) {
  if (scenariosCount === 0) {
    return {
      title:
        "No active FAIR scenarios are available for financial protection analysis.",
      description:
        "Coverage adequacy cannot be assessed until active scenarios have been quantified.",
      recommendation:
        "Review the FAIR scenario portfolio and activate the scenarios that require financing analysis.",
    };
  }

  if (uncoveredCount > 0) {
    return {
      title:
        "The current transfer portfolio leaves material digital-risk exposure unprotected.",
      description:
        `${uncoveredCount} of ${scenariosCount} active scenarios have no linked active coverage. ` +
        `Current arrangements protect ${formatPercent(
          protectionRatio,
          1
        )} of portfolio P95 exposure, leaving ${formatMoney(
          capitalAtRisk,
          currency
        )} of capital at risk.`,
      recommendation:
        "Prioritize the uncovered scenarios with the highest P95 exposure and evaluate whether mitigation, additional transfer capacity, or explicit risk retention provides the best capital outcome.",
    };
  }

  if (
    underinsuredCount > 0 ||
    marginalCount > 0
  ) {
    return {
      title:
        "Coverage exists, but the current protection position remains insufficient for some scenarios.",
      description:
        `${underinsuredCount} scenarios are underinsured and ${marginalCount} are marginal. ` +
        `${adequateCount} scenarios currently meet the nominal P95 protection threshold.`,
      recommendation:
        "Evaluate targeted increases in limits, scenario-specific coverage adjustments, and control investments before purchasing broad additional capacity.",
    };
  }

  return {
    title:
      "The current transfer portfolio provides strong nominal protection across the active scenario portfolio.",
    description:
      `All ${scenariosCount} active scenarios meet the current P95 adequacy threshold, with ${formatPercent(
        protectionRatio,
        1
      )} of portfolio exposure protected.`,
    recommendation:
      "Review capacity utilization and overcapacity to determine whether premiums and limits remain economically efficient.",
  };
}

function normalizeRatio(value) {
  const number = Number(value ?? 0);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.min(
    Math.max(number, 0),
    1
  );
}